import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { sendPasswordResetEmail } from 'firebase/auth'
import { useAuthStore } from '../../stores/authStore'
import { auth } from '../../config/firebase'

const loginSchema = z.object({
  email: z.string().email('請輸入有效的 Email'),
  password: z.string().min(6, '密碼至少需要 6 個字元'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function LoginPage() {
  const navigate = useNavigate()
  const { signIn, loading } = useAuthStore()
  const [error, setError] = useState<string | null>(null)

  // 忘記密碼相關狀態
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    setError(null)
    try {
      await signIn(data.email, data.password)
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : '登入失敗')
    }
  }

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetMessage({ type: 'error', text: '請輸入 Email' })
      return
    }

    setResetLoading(true)
    setResetMessage(null)

    try {
      await sendPasswordResetEmail(auth, resetEmail)
      setResetMessage({ type: 'success', text: '重設密碼郵件已發送，請檢查您的信箱' })
      setResetEmail('')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '發送失敗'
      if (errorMessage.includes('user-not-found')) {
        setResetMessage({ type: 'error', text: '找不到此 Email 的帳號' })
      } else {
        setResetMessage({ type: 'error', text: errorMessage })
      }
    } finally {
      setResetLoading(false)
    }
  }

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false)
    setResetEmail('')
    setResetMessage(null)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: 'primary.main' }}
            >
              NurseBridge
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              管理後台登入
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register('email')}
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={loading}
            />
            <TextField
              {...register('password')}
              label="密碼"
              type="password"
              fullWidth
              margin="normal"
              error={!!errors.password}
              helperText={errors.password?.message}
              disabled={loading}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : '登入'}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setResetDialogOpen(true)}
                sx={{ cursor: 'pointer' }}
              >
                忘記密碼？
              </Link>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* 忘記密碼對話框 */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>重設密碼</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            請輸入您的 Email，我們將發送重設密碼的連結給您。
          </Typography>

          {resetMessage && (
            <Alert severity={resetMessage.type} sx={{ mb: 2 }}>
              {resetMessage.text}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={resetLoading}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} disabled={resetLoading}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={resetLoading || !resetEmail}
          >
            {resetLoading ? <CircularProgress size={20} /> : '發送重設連結'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
